import React from 'react';
import {
  ArrowRight,
  CheckCircle2,
  CircleDashed,
  ExternalLink,
  FileCheck2,
  LoaderCircle,
  RefreshCcw,
  Search,
  TriangleAlert,
} from 'lucide-react';

import type {
  EconomicResearchEvidenceStep,
  EconomicResearchResultSnapshot,
  EconomicResearchRunView,
} from '../../../types/economicResearch';
import { cn } from '../../../lib/utils';
import { Button } from '../../ui/button';
import {
  economicResearchTransportErrorMessage,
  type EconomicEvidenceResearchController,
} from './useEconomicEvidenceResearch';

function formattedDate(value: string | undefined): string | null {
  if (!value) return null;
  const timestamp = Date.parse(value);
  if (!Number.isFinite(timestamp)) return value;
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(new Date(timestamp));
}

function basisSummary(snapshot: EconomicResearchResultSnapshot | undefined): string | null {
  if (!snapshot) return null;
  const labels: Record<string, string> = {
    'verified-actual': 'verified actual',
    'observed-public-record': 'observed',
    committed: 'committed',
    announced: 'announced',
    'secondary-discovery': 'discovery only',
  };
  const parts = Object.entries(snapshot.basisCounts)
    .filter((entry): entry is [string, number] => typeof entry[1] === 'number' && entry[1] > 0)
    .map(([basis, count]) => `${count} ${labels[basis] ?? basis}`);
  return parts.length > 0 ? parts.join(' · ') : null;
}

function stateOf(controller: EconomicEvidenceResearchController, baselineRecordCount: number): string {
  if (!controller.run) {
    if (controller.starting) return 'starting';
    if (controller.transportError) return 'transport-failed';
    return baselineRecordCount > 0 ? 'catalog-ready' : 'not-checked';
  }
  if (controller.run.stage !== 'complete' && !controller.run.terminalResult) return 'running';
  return controller.run.terminalResult ?? 'failed';
}

export const EconomicEvidenceResearchStatus: React.FC<{
  research: EconomicEvidenceResearchController;
  baselineRecordCount?: number;
}> = ({ research, baselineRecordCount = 0 }) => {
  const { run } = research;
  const state = stateOf(research, baselineRecordCount);
  const active = state === 'starting' || state === 'running';
  const safeTransportError = research.transportError
    ? economicResearchTransportErrorMessage(research.transportError)
    : null;
  const staleAt = run ? Date.parse(run.staleAt) : Number.NaN;
  const stale = Boolean(run && !active && Number.isFinite(staleAt) && staleAt <= Date.now());
  const finishedAt = formattedDate(run?.finishedAt ?? run?.updatedAt);
  const basis = basisSummary(run?.resultSnapshot);
  const sourceFailures = run?.resultSnapshot?.sourceFailures ?? [];
  const failedSourceClasses = [...new Set(sourceFailures.map((failure) => failure.sourceClass))];
  const sourceFailureSummary = sourceFailures.length > 0
    ? `${sourceFailures.length} configured ${sourceFailures.length === 1 ? 'source was' : 'sources were'} unavailable (${failedSourceClasses.join(', ')}). Results cover only the sources that completed.`
    : null;
  const refreshLabel = run?.terminalResult === 'failed'
    ? 'Retry public-record research'
    : run?.terminalResult === 'complete-no-policy-valid-evidence'
      ? 'Research again'
      : 'Refresh public records';

  let title = 'Public records have not been checked';
  let description = research.eligibilityMessage
    ?? 'Search official dockets, incentive records, and compatible project filings using the site location. A recorded MW range improves peer matching but is not required.';
  let tone = 'text-text-primary';
  let icon: React.ReactNode = <CircleDashed className="h-4 w-4 text-text-muted" aria-hidden />;

  if (active) {
    title = 'Researching official records';
    description = run?.currentStageLabel || 'Starting the official-record search.';
    tone = 'text-primary';
    icon = <LoaderCircle className="h-4 w-4 animate-spin text-primary motion-reduce:animate-none" aria-hidden />;
  } else if (state === 'transport-failed') {
    title = 'Public-record refresh could not start';
    description = baselineRecordCount > 0
      ? `${baselineRecordCount} reviewed catalog ${baselineRecordCount === 1 ? 'record remains' : 'records remain'} available below. Retry the site-specific refresh when the service is reachable.`
      : 'Retry the site-specific official-record search when the service is reachable.';
    tone = 'text-danger';
    icon = <TriangleAlert className="h-4 w-4 text-danger" aria-hidden />;
  } else if (state === 'catalog-ready') {
    title = `${baselineRecordCount} reviewed catalog ${baselineRecordCount === 1 ? 'record' : 'records'} available`;
    description = 'The reviewed baseline is available below while the site-specific official-record refresh starts.';
    tone = 'text-success';
    icon = <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />;
  } else if (run?.terminalResult === 'complete-with-evidence') {
    const policyValidCount = run.resultSnapshot?.policyValidRecords.length ?? run.counts.recordsPromoted;
    title = `${policyValidCount} policy-valid ${policyValidCount === 1 ? 'record' : 'records'}`;
    description = basis ?? 'Policy-valid records are available for the current site match.';
    tone = 'text-success';
    icon = <CheckCircle2 className="h-4 w-4 text-success" aria-hidden />;
  } else if (run?.terminalResult === 'complete-needs-context') {
    title = run.counts.recordsHeld > 0
      ? `${run.counts.candidatesFound} ${run.counts.candidatesFound === 1 ? 'record' : 'records'} found · ${run.counts.recordsHeld} need context${sourceFailures.length > 0 ? ` · ${sourceFailures.length} ${sourceFailures.length === 1 ? 'source' : 'sources'} unavailable` : ''}`
      : `Search incomplete · ${sourceFailures.length} ${sourceFailures.length === 1 ? 'source' : 'sources'} unavailable`;
    description = run.counts.recordsHeld > 0
      ? `Held records remain excluded until their unresolved entity, phase, or extracted-claim basis checks pass. Project MW is not required to search.${baselineRecordCount > 0 ? ' Reviewed catalog records remain available below.' : ''}`
      : `No policy-valid record was returned by the sources that completed. Refresh to retry the unavailable sources.${baselineRecordCount > 0 ? ' Reviewed catalog records remain available below.' : ''}`;
    tone = 'text-warning';
    icon = <TriangleAlert className="h-4 w-4 text-warning" aria-hidden />;
  } else if (run?.terminalResult === 'complete-no-policy-valid-evidence') {
    title = baselineRecordCount > 0
      ? 'No additional site-specific record found'
      : 'No policy-valid record found';
    description = `The search checked ${run.counts.sourcesAttempted} official ${run.counts.sourcesAttempted === 1 ? 'source' : 'sources'}; this does not mean no record exists.${baselineRecordCount > 0 ? ' Reviewed catalog records remain available below.' : ''}`;
  } else if (run?.terminalResult === 'failed') {
    title = 'Public-record research failed';
    description = run.failedSourceClass
      ? `${run.failedSourceClass} could not be checked. ${baselineRecordCount > 0 ? 'Reviewed catalog records remain available below.' : 'Existing findings remain unchanged.'}`
      : `The research run did not finish. ${baselineRecordCount > 0 ? 'Reviewed catalog records remain available below.' : 'Existing findings remain unchanged.'}`;
    tone = 'text-danger';
    icon = <TriangleAlert className="h-4 w-4 text-danger" aria-hidden />;
  }

  return (
    <section
      className="mt-4 border-y border-border-subtle py-3"
      data-testid="economic-evidence-research-status"
      data-state={state}
      data-stale={stale ? 'true' : 'false'}
      aria-busy={active}
      aria-label="Public-record research"
    >
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {icon}
            <p
              className={cn('text-sm font-semibold', tone)}
              role="status"
              aria-live="polite"
              aria-atomic="true"
            >
              {title}
            </p>
          </div>
          <p className="mt-1 max-w-[70ch] text-xs leading-relaxed text-text-secondary">{description}</p>
          {sourceFailureSummary && !active ? (
            <p className="mt-1 max-w-[70ch] text-xs leading-relaxed text-warning" role="alert">
              {sourceFailureSummary}
            </p>
          ) : null}
          {run?.currentSourceLabel && active ? (
            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.08em] text-text-muted">
              Current source · {run.currentSourceLabel}
            </p>
          ) : null}
          {finishedAt && !active ? (
            <p className="mt-1 font-mono text-[9px] uppercase tracking-[0.08em] text-text-muted">
              Research updated {finishedAt}
            </p>
          ) : null}
          {stale ? (
            <p className="mt-1 font-mono text-[9px] font-semibold uppercase tracking-[0.08em] text-warning">
              Refresh due · the source snapshot has passed its refresh date
            </p>
          ) : null}
        </div>

        {!run && research.eligible && !research.starting ? (
          <Button type="button" size="sm" onClick={() => void research.start()}>
            <Search aria-hidden /> {safeTransportError
              ? 'Retry public records'
              : baselineRecordCount > 0
                ? 'Refresh public records'
                : 'Find public records'}
          </Button>
        ) : run && !active && (run.terminalResult !== 'failed' || run.retryable) ? (
          <Button type="button" size="sm" variant="outline" disabled={research.refreshing} onClick={() => void research.refresh()}>
            <RefreshCcw className={cn(research.refreshing && 'animate-spin motion-reduce:animate-none')} aria-hidden />
            {research.refreshing ? 'Starting…' : refreshLabel}
          </Button>
        ) : null}
      </div>

      {run && active ? (
        <dl className="mt-3 grid grid-cols-3 divide-x divide-border-subtle border-t border-border-subtle pt-3" aria-label="Research progress counts">
          {[
            ['Sources checked', run.counts.sourcesAttempted],
            ['Candidates', run.counts.candidatesFound],
            ['Policy-valid', run.counts.recordsPromoted],
          ].map(([label, value]) => (
            <div key={String(label)} className="min-w-0 px-2 first:pl-0 last:pr-0">
              <dt className="truncate font-mono type-meta font-semibold tracking-[0.08em] text-text-muted">{label}</dt>
              <dd className="mt-0.5 text-sm font-semibold tabular-nums text-text-primary">{value}</dd>
            </div>
          ))}
        </dl>
      ) : null}

      {safeTransportError ? (
        <p className="mt-2 text-xs text-danger" role="alert">{safeTransportError}</p>
      ) : null}
    </section>
  );
};

const STEP_LABEL: Record<EconomicResearchEvidenceStep['kind'], string> = {
  'official-record': 'Official record',
  'extracted-claim': 'Extracted claim',
  'compatibility-decision': 'Compatibility decision',
};

const EvidenceTrail: React.FC<{
  label: string;
  status: 'accepted' | 'held' | 'excluded';
  steps: readonly EconomicResearchEvidenceStep[];
  reasons?: readonly string[];
}> = ({ label, status, steps, reasons = [] }) => (
  <section className="border-t border-border-subtle pt-3 first:border-0 first:pt-0" data-evidence-status={status}>
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h4 className="text-xs font-semibold text-text-primary">{label}</h4>
      <span className={cn(
        'font-mono text-[9px] font-semibold uppercase tracking-[0.09em]',
        status === 'accepted' ? 'text-success' : status === 'held' ? 'text-warning' : 'text-text-muted',
      )}>
        {status === 'accepted' ? 'Policy-valid' : status}
      </span>
    </div>
    <ol
      className="mt-3 grid min-w-0 grid-cols-1 gap-3 border-l border-border-subtle pl-3 lg:grid-cols-3 lg:gap-2 lg:border-l-0 lg:border-t lg:pl-0 lg:pt-3"
      aria-label={`${label} evidence trail`}
      data-testid="economic-evidence-trail"
    >
      {steps.map((step, index) => (
        <li key={`${step.kind}:${index}`} className="relative min-w-0 lg:pr-4">
          <div className="flex items-center gap-1.5">
            <span className="font-mono type-meta font-semibold tracking-[0.08em] text-primary">{STEP_LABEL[step.kind]}</span>
            {index < steps.length - 1 ? <ArrowRight className="hidden h-3 w-3 text-text-muted lg:block" aria-hidden /> : null}
          </div>
          <p className="mt-1 break-words text-[11px] font-semibold leading-relaxed text-text-primary">{step.label}</p>
          <p className="mt-0.5 break-words text-[10px] leading-relaxed text-text-muted">{step.detail}</p>
          {step.excerpt ? (
            <blockquote className="mt-1 border-l border-border-subtle pl-2 text-[10px] leading-relaxed text-text-secondary">
              “{step.excerpt}”
            </blockquote>
          ) : null}
          {step.locator ? <p className="mt-1 font-mono type-caption text-text-muted">{step.locator}</p> : null}
          {step.sourceUrl ? (
            <a
              href={step.sourceUrl}
              target="_blank"
              rel="noreferrer"
              className="mt-1 inline-flex items-center gap-1 break-all font-mono text-[9px] font-semibold uppercase tracking-[0.07em] text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-primary/40"
            >
              Open record <ExternalLink className="h-3 w-3" aria-hidden />
            </a>
          ) : null}
        </li>
      ))}
    </ol>
    {reasons.length > 0 ? (
      <p className="mt-2 text-[10px] leading-relaxed text-text-muted">{reasons.join(' · ')}</p>
    ) : null}
  </section>
);

export const EconomicEvidenceTrailList: React.FC<{
  run: EconomicResearchRunView;
}> = ({ run }) => <EconomicEvidenceTrailSnapshotList snapshot={run.resultSnapshot} />;

export const EconomicEvidenceTrailSnapshotList: React.FC<{
  snapshot: EconomicResearchResultSnapshot | undefined;
}> = ({ snapshot }) => {
  if (!snapshot) return null;
  const accepted = snapshot.policyValidRecords.slice(0, 5);
  const held = snapshot.held.slice(0, 5);
  const excluded = snapshot.excluded.slice(0, 5);
  const total = accepted.length + held.length + excluded.length;
  if (total === 0) return null;
  return (
    <div className="space-y-4" data-testid="economic-evidence-trails">
      <div className="flex items-center gap-2 text-[10px] text-text-muted">
        <FileCheck2 className="h-3.5 w-3.5 text-primary" aria-hidden />
        <span>Official record → extracted claim → compatibility decision</span>
      </div>
      {accepted.map((record) => (
        <EvidenceTrail
          key={`accepted:${record.projectId}`}
          label={record.facilityName}
          status="accepted"
          steps={record.evidenceTrail}
          reasons={record.reasons}
        />
      ))}
      {held.map((lead) => (
        <EvidenceTrail
          key={`held:${lead.leadId}`}
          label={lead.candidateLabel || lead.sourceLabel}
          status="held"
          steps={lead.evidenceTrail}
          reasons={lead.reasons}
        />
      ))}
      {excluded.map((lead) => (
        <EvidenceTrail
          key={`excluded:${lead.leadId}`}
          label={lead.candidateLabel || lead.sourceLabel}
          status="excluded"
          steps={lead.evidenceTrail}
          reasons={lead.reasons}
        />
      ))}
      <p className="sr-only">{total} evidence trails shown.</p>
    </div>
  );
};
